// @givmo/app-charity — where the gear was pressed: the settings page's ORIGIN (D-CPO-33; the OM-9
// build — "the sidebar framed by the gear's ORIGIN"). The company console's twin, verbatim.
//
// The gear's page is composed for the person from the module they were standing on, and its sidebar
// wears that page's frame, so the page has to know which address the gear was pressed on. That
// address — and the frame the page wore — ride the settings page's own ADDRESS
// (`/settings?from=…&frame=…`, Org Machinery OM-28; the package's `settings-origin-address.ts`):
// on the gear's navigate, and in the `href` of both doors, so a reload, a new tab, a bookmark or a
// shared link opens the same page. History state, which carried the origin alone before OM-28, is
// still READ for an entry an earlier build wrote. A bare `/settings` — a typed address — reads as
// "origin unknown": no module section but the cold-load rule's, the shell's own frame rule for the
// sidebar. Never a storage key, never a module-level recorder.

import { useNavigate, useRouterState } from '@tanstack/react-router';
import {
  SETTINGS_FRAME_SEARCH_KEY,
  SETTINGS_ORIGIN_SEARCH_KEY,
  settingsFrameFromAddress,
  settingsOriginFromAddress,
  settingsOriginHref,
  settingsOriginSearchFor,
} from '@givmo/org-console';
import { useCallback } from 'react';
import { SETTINGS_ROUTE } from './console-routes';
import { carriedFrameOf } from './sidebar-frame';

declare module '@tanstack/react-router' {
  interface HistoryState {
    /** The pathname the gear was pressed on — the settings page's origin (D-CPO-33). */
    settingsOrigin?: string;
    /**
     * The FRAME that page wore, beside the address (Org Machinery OM-18 design 4(d)): a slug, or
     * `null` when it wore none. A page BORROWED into another module's frame re-enters that frame
     * exactly — a stale hint notwithstanding — and the composed page's module section is the
     * frame's, not the address's own module.
     */
    settingsFrame?: string | null;
  }
}

/** The address the settings page was opened from, read tolerantly off history state; `null` when none. */
export function settingsOriginOf(state: unknown): string | null {
  if (typeof state !== 'object' || state === null) return null;
  const origin = (state as Record<string, unknown>).settingsOrigin;
  return typeof origin === 'string' && origin.startsWith('/') ? origin : null;
}

/** The frame the gear's origin wore: a slug, `null` for none, `undefined` when not carried. */
export function settingsFrameOf(state: unknown): string | null | undefined {
  return carriedFrameOf(state, 'settingsFrame');
}

/** A location's search value under `key`, or `undefined` — the router hands the parsed object. */
function searchValue(search: unknown, key: string): unknown {
  return typeof search === 'object' && search !== null
    ? (search as Record<string, unknown>)[key]
    : undefined;
}

/**
 * The settings page's origin for THIS location — the address's (OM-28), else an earlier build's
 * history state — or `null` on a cold load.
 */
export function useSettingsOrigin(): string | null {
  return useRouterState({
    select: (state) =>
      settingsOriginFromAddress(searchValue(state.location.search, SETTINGS_ORIGIN_SEARCH_KEY)) ??
      settingsOriginOf(state.location.state),
  });
}

/**
 * The frame the settings page's origin wore, for THIS location: the address's, else history
 * state's. An address that names an origin and no frame carried none (`null`).
 */
export function useSettingsFrame(): string | null | undefined {
  return useRouterState({
    select: (state) => {
      const search = state.location.search;
      if (settingsOriginFromAddress(searchValue(search, SETTINGS_ORIGIN_SEARCH_KEY)) !== null) {
        return settingsFrameFromAddress(searchValue(search, SETTINGS_FRAME_SEARCH_KEY)) ?? null;
      }
      return settingsFrameOf(state.location.state);
    },
  });
}

/**
 * The settings page's address for a door pressed on `from`, wearing `frame` — the `href` the gear,
 * the sidebar's Settings row and a module's own settings door carry, so a new tab opens the page the
 * in-app navigate does.
 */
export function settingsHref(from: string, frame: string | null = null): string {
  return settingsOriginHref(SETTINGS_ROUTE, from, frame);
}

/**
 * The gear's navigate: open the settings page, carrying in its address the pathname it was pressed
 * on and the frame that page wore (none from the Admin Console family or the floor).
 */
export function useGoToSettings(): (from: string, frame?: string | null) => void {
  const navigate = useNavigate();
  return useCallback(
    (from: string, frame: string | null = null) => {
      void navigate({ to: SETTINGS_ROUTE, search: settingsOriginSearchFor(from, frame) });
    },
    [navigate],
  );
}
